// noinspection SpellCheckingInspection

import {BaseStage} from "./BaseStage";
import {loading, stopLoading} from "../../../helpers/loading";
import Axios from "axios";
import handleAjaxErrorV2 from "../../../helpers/handleAjaxErrorV2";
import createElement from "../../../helpers/createElement";
import Panzoom from '@panzoom/panzoom'

class ReservedSeatsSelectStage extends BaseStage {
    /** @var {number} Breakpoint for small devices */
    static BREAKPOINT_XSM = 380;
    /** @var {number} Breakpoint for small devices */
    static BREAKPOINT_SM = 576;
    /** @var {number} Breakpoint for medium devices */
    static BREAKPOINT_MD = 768;
    /** @var {number} Breakpoint for large devices */
    static BREAKPOINT_LG = 992;
    /** @var {number} Breakpoint for extra large devices */
    static BREAKPOINT_XL = 1200;

    /** @var {HTMLElement} */
    templatesContainer;

    /** @var {HTMLElement} */
    stageWrapper;

    /** @var {HTMLElement} */
    seatMapContainer;
    /** @var {HTMLElement} */
    seatMapSvgContainer;
    /** @var {HTMLElement} */
    seatMapWrapper;
    /** @var {HTMLElement} */
    selectedSeatsIndicator;
    /** @var {HTMLElement} */
    clearSelectionButton;
    /** @var {HTMLIFrameElement} */
    legacyMapIframe;
    /** @var {import('@panzoom/panzoom').PanzoomObject} */
    panzoomInstance;

    /** @var {Object} */
    seatPointerDownCache = {};

    animationHookActive = false;

    /** @var {ReservedSeatsSelectStageDefs.HandicapSeatDefinition[]} */
    handicapSeats;

    /**
     * @type {Object<string,Object>}
     */
    handicapSeatCompanionPopupsData = {};

    constructor(globalState) {
        super(globalState);

        // Init dom container
        this.domContainer = document.querySelector(`.stage.${this.getStageKey()}`)

        // Retrieve template container
        this.templatesContainer = document.querySelector('.templates .reserved-seats-select-stage-template');

        // Clone template
        this.stageWrapper = this.templatesContainer.querySelector('.stage-wrapper').cloneNode(true);

        this.seatMapContainer = this.stageWrapper.querySelector('.seat-map-container');
        this.seatMapWrapper = this.stageWrapper.querySelector('.seat-map-wrapper');
        this.selectedSeatsIndicator = this.stageWrapper.querySelector('.selected-seats-indicator');
        this.clearSelectionButton = this.selectedSeatsIndicator.querySelector('.clear-selection-button');

        this.clearSelectionButton.addEventListener('click', () => {
            this.clearSelection();
        })

        // Append stage to DOM
        this.domContainer.appendChild(this.stageWrapper);

        // Run base class initializations
        this.initSharedComponents();

        this.getSeatMapHtml();
    }

    getSeatMapHtml() {
        loading();

        Axios.post(this.globalState.selectedDate.getSeatMapRoute)
            .then(response => {
                // Set seat map HTML
                this.seatMapContainer.innerHTML = response.data.map;
                this.seatMapSvgContainer = this.seatMapContainer.querySelector('.seat-map-svg-container');

                // Initialize html
                this.initSeatMapHtml();

                stopLoading();
            })
            .catch(error => {
                handleAjaxErrorV2(error);
                stopLoading()
            })
    }

    /**
     * @return {PublicTicketsFormDefs.Stage}
     */
    getStageKey() {
        // noinspection JSValidateTypes
        return 'seat-select';
    }

    clearDom() {
        // Kill panzoom
        this.panzoomInstance.destroy();

        // Clear selected seats from global state
        this.globalState.setSelectedSeats([]);

        // Clear any zoom interval left over
        try {
            clearInterval(this.clearZoomGuideIntervalId);
        } catch (e) {

        }

        // Clear stage HTML
        this.domContainer.innerHTML = '';
    }

    initSeatMapHtml() {
        // Init panzoom
        this.panzoomInstance = Panzoom(
            this.seatMapSvgContainer,
            {
                maxScale: 4.5,
                minScale: 0.8,
            }
        )

        this.setInitialScaleAndPan();

        // Bind zoom events
        // noinspection JSCheckFunctionSignatures
        this.seatMapSvgContainer.addEventListener('wheel', (e, options) => this.panzoomZoomWithWheelsWrapper(e, options))

        // Bind seat selection events
        this.bindSeatSelectionEvents();

        // Wait for scale change to hide zoom indicator
        setTimeout(() => {
            try {
                const initialZoomScale = this.getCurrentMapScale();
                this.clearZoomGuideIntervalId = setInterval(
                    () => {
                        // User has zoomed in or out, clear indicator
                        if (Math.abs(initialZoomScale - this.getCurrentMapScale()) > 0.1) {
                            this.clearZoomIndicatorGuide();
                            clearInterval(this.clearZoomGuideIntervalId);
                        }
                    },
                    2000,
                )
            } catch (e) {
                // Soemthing went wrong, remove it after 15 seconds
                setTimeout(() => {
                    this.clearZoomIndicatorGuide();
                }, 1000)

                console.error('Error setting up interval for clearing zoom guide');
                console.error(e);
            }
        }, 500);
    }

    getCurrentViewportSize() {
        const width = window.innerWidth;
        if (width < ReservedSeatsSelectStage.BREAKPOINT_XSM) return 'xxs';
        if (width < ReservedSeatsSelectStage.BREAKPOINT_SM) return 'xs';
        if (width < ReservedSeatsSelectStage.BREAKPOINT_MD) return 'sm';
        if (width < ReservedSeatsSelectStage.BREAKPOINT_LG) return 'md';
        if (width < ReservedSeatsSelectStage.BREAKPOINT_XL) return 'lg';
        return 'xl';
    }

    setInitialScaleAndPan() {
        // Wait for panzoom to be ready
        setTimeout(() => {
            const viewport = this.getCurrentViewportSize();

            switch (viewport) {
                case 'xxs':
                    this.panzoomInstance.zoom(0.7);
                    break;
                case 'xs':
                    this.panzoomInstance.zoom(0.9);
                    break;
                case 'sm':
                    this.panzoomInstance.zoom(1.1);
                    break;
                case 'md':
                    this.panzoomInstance.zoom(1.3);
                    break;
                case 'lg':
                case 'xl':
                    this.panzoomInstance.zoom(1.50);
                    break;
            }

            const coords = this.getCenteredTransform(this.seatMapSvgContainer.querySelector('svg'), 1);

            this.panzoomInstance.pan(coords.x, coords.y);

            // Make visible
            this.seatMapSvgContainer.classList.remove('opacity-0');
        }, 256)
    }

    getCenteredTransform(element, scale = 1) {
        // TODO: This should be retrieved from the map element itself
        const xOffset = -25;
        const yOffset = 70;

        // Get the container's position and size
        const containerRect = this.seatMapSvgContainer.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        // Calculate container center
        const containerCenterX = containerWidth / 2;
        const containerCenterY = containerHeight / 2;

        const rect = element.getBoundingClientRect();
        const elementWidth = rect.width;
        const elementHeight = rect.height;

        // Calculate element center after scaling
        const scaledElementWidth = elementWidth * scale;
        const scaledElementHeight = elementHeight * scale;
        const elementCenterX = scaledElementWidth / 2;
        const elementCenterY = scaledElementHeight / 2;

        // Calculate translation relative to container
        const translateX = containerCenterX - elementCenterX - (rect.left - containerRect.left) + xOffset;
        const translateY = containerCenterY - elementCenterY - (rect.top - containerRect.top) + yOffset;

        return {x: translateX, y: translateY};
    }

    clearZoomIndicatorGuide() {
        const zoomIndicatorElement = this.stageWrapper.querySelector('.zoom-indicator');
        if (zoomIndicatorElement) {
            // First hide it for animation
            zoomIndicatorElement.style.opacity = 0;

            // Then truly it hide it
            setTimeout(() => {
                zoomIndicatorElement.classList.add('d-none');
            }, 1000)
        }
    }

    getCurrentMapScale() {
        return parseFloat(this.seatMapSvgContainer.style.transform.match(/scale\(([+-]?\d+\.?\d*)\)/)[1]);
    }

    panzoomZoomWithWheelsWrapper(event, zoomOptions) {
        return this.panzoomInstance.zoomWithWheel(event, zoomOptions);
    }

    /**
     * @return {NodeListOf<Element>}
     */
    getAvailableSeatElements() {
        return this.seatMapSvgContainer.querySelectorAll('.seat.available');
    }

    clearSelection() {
        // Toggle all selected seats
        this.seatMapSvgContainer.querySelectorAll('.seat[data-selected="true"]')
            .forEach(seat => {
                this.toggleSeatSelection(seat);
            });
    }

    bindSeatSelectionEvents() {
        const availableSeats = this.seatMapSvgContainer.querySelectorAll('.seat.available:not(.initialized)');

        availableSeats.forEach(seat => {
            seat.addEventListener('pointerdown', (event) => this.handleSeatPointerDown(event));
            seat.addEventListener('pointerup', (event) => this.handleSeatPointerUp(event));

            // Mark as initialized
            seat.classList.add('initialized');
        });
    }

    handleSeatPointerDown(event) {
        const seatKey = event.currentTarget.dataset.seatKey;
        if (!seatKey) {
            return;
        }


        this.seatPointerDownCache[seatKey] = {
            x: event.screenX,
            y: event.screenY,
            timestamp: Date.now()
        };
    }

    /**
     * @param {MouseEvent|Event} event
     */
    handleSeatPointerUp(event) {
        const seatKey = event.currentTarget.dataset.seatKey;
        if (!seatKey || !this.seatPointerDownCache[seatKey]) {
            return;
        }

        const pointerDownData = this.seatPointerDownCache[seatKey];
        const currentTime = Date.now();
        const timeDiff = currentTime - pointerDownData.timestamp;
        const xDiff = Math.abs(event.screenX - pointerDownData.x);
        const yDiff = Math.abs(event.screenY - pointerDownData.y);

        // Check if this was a "click" (within threshold and time limit)
        const threshold = 5; // 5px threshold
        const timeThreshold = 500; // 500ms threshold

        if (xDiff <= threshold && yDiff <= threshold && timeDiff <= timeThreshold) {
            // This was a tap/click, toggle selection
            if (event.altKey || event.ctrlKey || event.metaKey) {
                this.toggleRowSelection(event.currentTarget);
            } else {
                this.toggleSeatSelection(event.currentTarget);
            }
        }

        // Clean up cache entry
        delete this.seatPointerDownCache[seatKey];
    }

    /**
     * @param seatElement
     */
    toggleRowSelection(seatElement) {
        // First check if key matches something like [ID]_[ROW_INDEX]_[SEAT_INDEX] format
        const format = /(.*[_-])(\d+)$/;

        // Not expected format in key, just treat it as a normal seat toggle
        const match = format.exec(seatElement.dataset.seatKey);

        if (match === null) {
            this.toggleSeatSelection(seatElement);
            return;
        }

        // Split seat index from the rest of the key
        const prefix = match[1];

        // Get all seats that match this prefix
        const seatsWithSharedPrefix = Array.from(this.getAvailableSeatElements())
            .filter(seatElement => {
                const seatKey = seatElement.dataset.seatKey ?
                    seatElement.dataset.seatKey : '';

                return seatKey.startsWith(prefix);
            })

        // Toggle all matched elements, hopefully, all in the same row
        seatsWithSharedPrefix.forEach(seatElement => {
            this.toggleSeatSelection(seatElement, true);
        })

        // Trigger seat update
        this.onSeatsChange();
    }


    /**
     * @param seatElement
     * @param silent Skips calling on seats change event
     */
    toggleSeatSelection(seatElement, silent = false) {
        const isSelected = seatElement.dataset.selected === 'true';

        if (isSelected) {
            seatElement.classList.remove('selected');
            seatElement.dataset.selected = 'false';
        } else {
            seatElement.classList.add('selected');
            seatElement.dataset.selected = 'true';
        }

        if (silent === false) {
            // Trigger seats change event
            this.onSeatsChange();
        }
    }

    onSeatsChange() {
        // Get seats from DOM
        /** @var {PublicTicketsFormDefs.ReservedSeatSelection[]} */
        const seats = [];

        this.seatMapSvgContainer.querySelectorAll('.seat[data-selected="true"]')
            .forEach(seat => {
                seats.push({
                    seat_key: seat.dataset.seatKey,
                    seat_group_id: seat.dataset.groupId,
                    is_handicap: seat.dataset.isHandicap === '1',
                    companion_seat_key: seat.dataset.companionSeatKey,
                })
            });

        // Check for new seat selections before updating state
        const currentSeats = this.globalState.selectedSeats || [];
        const newSeats = seats.filter(newSeat =>
            !currentSeats.some(currentSeat => currentSeat.seat_key === newSeat.seat_key)
        );

        // Handle custom behavior for new selections
        newSeats.forEach(newSeat => {
            this.onNewSeatSelection(newSeat);
        });

        // Update state
        this.globalState.setSelectedSeats(seats);

        this.updateSelectedSeatsIndicator();

        // Enable/disable next stage button
        if (this.globalState.selectedSeats.length) {
            this.enableNextStageButton();
        } else {
            this.disableNextStageButton();
        }
    }

    updateSelectedSeatsIndicator() {
        const seatsCount = this.globalState.selectedSeats.length;

        this.selectedSeatsIndicator.querySelector('.text').innerHTML =
            `${seatsCount} ${seatsCount === 1 ? 'seat' : 'seats'} selected`;

        this.clearSelectionButton.classList.toggle('hidden', seatsCount < 1);
    }

    // noinspection JSUnusedLocalSymbols
    /**
     * Called when a new seat has been selected
     *
     * @param {PublicTicketsFormDefs.ReservedSeatSelection} selectedSeat
     */
    onNewSeatSelection(selectedSeat) {
        if (selectedSeat.is_handicap) {
            const companionSeatKey = selectedSeat.companion_seat_key;

            if (companionSeatKey) {
                const companionSeatElement = this.domContainer.querySelector(`.seat:not(.taken)[data-seat-key$=${companionSeatKey}]`)


                if (companionSeatElement && companionSeatElement.dataset.selected !== 'true') {
                    // Create tooltip
                    const tooltipElement = createElement(`
                            <div class="alert alert-info m-0 p-2 handicap-tooltip handicap-companion-seat-popper-tooltip">
                                <div class="tooltip-body">
                                    <div class="text">
                                        You've chosen a wheelchair spot. <br>
                                        Select <b>campanion seat</b> also?
                                    </div>
                                    <div class="d-flex buttons">
                                        <button class="btn btn-sm btn-success yes-button flex-grow-1"><b>YES</b></button>
                                        <button class="btn btn-sm btn-light no-button ml-2 w-auto px-3">No</button>
                                    </div>
                                </div>
                                <div class="arrow" data-popper-arrow></div>
                            </div>
                        `);

                    tooltipElement.querySelector('.yes-button').addEventListener('click', () => {
                        if (companionSeatElement.dataset.selected !== 'true') {
                            this.toggleSeatSelection(companionSeatElement);
                        }
                        // And kill yourself
                        this.handicapSeatCompanionPopupsData[companionSeatKey].element.remove();
                        this.handicapSeatCompanionPopupsData[companionSeatKey].popperInstance.destroy();
                        delete this.handicapSeatCompanionPopupsData[companionSeatKey];
                    })

                    tooltipElement.querySelector('.no-button').addEventListener('click', () => {
                        // kill yourself
                        this.handicapSeatCompanionPopupsData[companionSeatKey].element.remove();
                        this.handicapSeatCompanionPopupsData[companionSeatKey].popperInstance.destroy();
                        delete this.handicapSeatCompanionPopupsData[companionSeatKey];
                    })

                    // Append to dom
                    this.seatMapWrapper.appendChild(tooltipElement);

                    // Save in memory
                    this.handicapSeatCompanionPopupsData[companionSeatKey] = {
                        element: tooltipElement,
                        popperInstance: window.Popper.createPopper(
                            companionSeatElement,
                            tooltipElement,
                            {
                                placement: 'top',
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: [0, 12],
                                        },
                                    },
                                ],
                            },
                        ),
                    }

                    this.handicapSeatCompanionPopupsData[companionSeatKey].popperInstance.update();
                }
            }

        }
    }

    display() {
        super.display();

        if (this.animationHookActive === false) {
            // Re enable hook
            this.onAnimationFrame();
        }
    }

    /**
     * Called on each animation frame while the stage is active
     */
    onAnimationFrame() {
        this.animationHookActive = true;

        if (
            !this.domContainer || // If there is no container
            this.domContainer.innerHTML === '' || // Or has been cleared
            this.globalState.currentStage !== 'seat-select' // Or it is no longer at the seat stage
        ) {
            // Stage is gone, stop animating
            this.animationHookActive = false;
            return;
        }

        this.updateCompanionSeatTooltips();

        // Queue next frame
        requestAnimationFrame(() => {
            this.onAnimationFrame();
        })
    }


    /**
     * Updates the position of the companion seats popups that are live
     */
    updateCompanionSeatTooltips() {
        Object.values(this.handicapSeatCompanionPopupsData).forEach(popupData => {
            popupData.popperInstance.update();
        });
    }

}

export {
    ReservedSeatsSelectStage
};
